import { AirportMaintenanceService } from './../../service/airport-maintenance.service';
import { FacilityService } from 'src/app/core/services/facility.service';
import { CityOptions } from './../../../../core/models/options/city-options';
import { AirportMaintenanceTableData } from './interfaces/airport-maintenance-table-data';
import { PageInfo } from './../../../../core/models/page-info';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { throwError } from 'rxjs';
import { deepCopy, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { LaasOption } from 'src/app/core/models/laas-option';
import { DateTableColumn } from 'src/app/core/models/date-table-column';
import { AirportMaintenanceGetRequest } from './interfaces/airport-maintenance-get-request';
import { AirportMaintenanceSaveRequest } from './interfaces/airport-maintenance-save-request';
import { PageResp } from 'src/app/core/models/page/page-response';
import { AirportMaintenanceSaveBean } from './interfaces/airport-maintenance-save-bean';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

@Component({
  selector: 'laas-ariport-maintenance',
  templateUrl: './ariport-maintenance.component.html',
  styleUrls: ['./ariport-maintenance.component.scss']
})
export class AirportMaintenanceComponent extends SessionBaseComponent implements OnInit {
  readonly viewPermissionCode = PermissionConstants.AirportMaintenance;
  currentLang: string = this.laasSetting.language;
  // Status for the Query Form is open or not
  isSearchFormOpen = true;
  // The formGroup for the Query Form part
  searchFormGroup: FormGroup;

  cityOptions: CityOptions[] = []
  isActivatedOptions: LaasOption[] = []

  // Status for the loading circle on the table
  isLoading: boolean = false;
  // Status for enabling a user to edit the data on the table
  isInEditMode: boolean = false;
  // The table for table part
  @ViewChild('table') table!: Table;
  // All the columns of the table have
  tableColumns: DateTableColumn[] = [
    { field: 'airportCode', header: 'AirportMaintenance.Table.ColumnHeader.AirportCode', sort: true },
    { field: 'airportName', header: 'AirportMaintenance.Table.ColumnHeader.AirportName', sort: true },
    { field: 'locatedCityName', header: 'AirportMaintenance.Table.ColumnHeader.LocatedCityName', sort: true },
    { field: 'isActivated', header: 'AirportMaintenance.Table.ColumnHeader.IsActivated', sort: true },
    { field: 'createUser', header: 'AirportMaintenance.Table.ColumnHeader.CreateUser', sort: true },
    { field: 'createDt', header: 'AirportMaintenance.Table.ColumnHeader.CreateDt', sort: true },
    { field: 'updateUser', header: 'AirportMaintenance.Table.ColumnHeader.UpdateUser', sort: true },
    { field: 'updateDt', header: 'AirportMaintenance.Table.ColumnHeader.UpdateDt', sort: true },
  ];
  // The columns user choose to display
  selectedTableColumns: DateTableColumn[] = [];
  // Current data on the table
  tableData: AirportMaintenanceTableData[] = [];
  // Backup data when data retrieved from the back-end
  backupTableData: AirportMaintenanceTableData[] = [];
  // User current selected row on the table
  selectedRow: AirportMaintenanceTableData | null;
  // User last selected row on the table
  previousSelectedRow: AirportMaintenanceTableData | null;

  // Records for user's operation on the table
  newDatas: AirportMaintenanceSaveBean[] = [];
  updatedDatas: AirportMaintenanceSaveBean[] = [];
  deletedDataSeqNos: number[] = [];

  // For data key to distinguish the added rows on the table
  newDataKeyId: number = 0;

  // Table paging info
  pageInfo: PageInfo = {
    pageIndex: 0,
    pageSize: 5,
    totalRecords: 0,
    totalPages: 0,
  };
  // Table sorting info
  sortOrder: SortOrder = {
    property: "SeqNo",
    direction: -1
  };

  constructor(
    private injector: Injector,
    private formBuilder: FormBuilder,
    private facilityService: FacilityService,
    private airportMaintenanceService: AirportMaintenanceService,
  ) {
    super(injector);

    this.searchFormGroup = this.formBuilder.group({
      airportCode: null,
      airportName: null,
      locatedCitySeqNo: null
    })

    this.initAllOption();

    this.selectedTableColumns = this.tableColumns;
    this.selectedRow = null;
    this.previousSelectedRow = this.selectedRow;
  }

  //#region initial Option

  private initAllOption() {
    this.initCityOption();
    this.initIsActivatedOption();
  }

  private initCityOption() {
    this.facilityService.getCityOptions().subscribe({
      next: (options) => {
        for (let option of options) {
          this.cityOptions.push({
            seqNo: option.seqNo,
            country: option.country,
            name: `${option.name}-${option.enName}`,
            enName: option.enName
          })
        }
      }
    })
  }

  private initIsActivatedOption() {
    this.isActivatedOptions = [
      { label: 'AirportMaintenance.Option.IsActivated.Activated', value: 1 },
      { label: 'AirportMaintenance.Option.IsActivated.Deactivated', value: 0 }
    ]
  }

  //#endregion initial Option

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  @Input() get selectedColumns(): DateTableColumn[] {
    return this.selectedTableColumns;
  }

  set selectedColumns(cols: DateTableColumn[]) {
    // Filter display columns
    this.selectedTableColumns = this.tableColumns.filter(col => cols.includes(col));
  }

  // Handle query button click on the query form
  searchBtnClick(): void {
    this.isSearchFormOpen = false;
    this.isInEditMode = false;
    this.isLoading = true;
    this.pageInfo.pageIndex = 0;
    this.table.first = 0;
    this.sendSearchRequest();
  }

  // Send Get request to service class for retrieve data
  sendSearchRequest(): void {
    this.restoreData();
    const request: AirportMaintenanceGetRequest = this.createGetRequestBean();
    this.airportMaintenanceService.findAirport(request).subscribe({
      next: (response) => {
        this.table.cancelRowEdit(this.selectedRow);
        if (!isNullOrEmpty(response) && !isNullOrEmpty(response.content)) {
          this.setDataByServiceResponse(response);
        }
        else {
          throwError(response);
        }
      },
      error: (response) => {
        this.clearTable();
        this.toastService.error(this.translateService.instant('AirportMaintenance.Toast.GetDataFail') + response.status);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    })
  }

  // Handle add button click on the table
  addNewRow(): void {
    // Add a new row to table's first row
    let newAirportData = new AirportMaintenanceTableData();
    newAirportData.createUser = this.userService.userInfo.userName;
    newAirportData.createDt = new Date();
    newAirportData.updateUser = this.userService.userInfo.userName;
    newAirportData.updateDt = new Date();
    newAirportData.dataKey = `--${this.newDataKeyId++}`;

    this.tableData.splice(0, 0, newAirportData)
    this.clearTableEdit()
    this.table.initRowEdit(this.selectedRow);
  }

  deleteSelectedRow(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('AirportMaintenance.Confirm.Header'),
      message: this.translateService.instant('AirportMaintenance.Confirm.Delete'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.table.cancelRowEdit(this.selectedRow);

        if (!isNullOrEmpty(this.selectedRow)) {
          const selectedRowIndex = this.tableData.indexOf(this.selectedRow!);
          if (selectedRowIndex > -1) {
            this.addToDeletedList(this.tableData[selectedRowIndex].seqNo)
          }

          // Remove deleted row from table
          this.tableData.splice(selectedRowIndex, 1);

          // Change selected row
          if (selectedRowIndex != this.tableData.length) {
            // Change user current selected row to the next row of the deleted row
            this.selectedRow = this.tableData[selectedRowIndex];
          }
          else { // Deleted row is the last row of the current page
            // Change user current selected row to the previous row of the deleted row
            this.selectedRow = this.tableData[selectedRowIndex - 1];
          }
          this.previousSelectedRow = this.selectedRow;
          this.updateTableHighlight(this.selectedRow);

          this.table.initRowEdit(this.selectedRow);
          this.toastService.success(this.translateService.instant("AirportMaintenance.Toast.DeleteSuccess"));
        }
      }
    })
  }

  saveChanges(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('AirportMaintenance.Confirm.Header'),
      message: this.translateService.instant('AirportMaintenance.Confirm.Save'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.isLoading = true;

        // Check user's operations on the table
        this.checkNewAndUpdatedRow();

        const savedData: AirportMaintenanceSaveRequest = {
          newDatas: this.newDatas,
          updatedDatas: this.updatedDatas,
          deletedDataSeqNos: this.deletedDataSeqNos
        }

        this.airportMaintenanceService.saveChanges(savedData).subscribe({
          next: () => {
            this.table.cancelRowEdit(this.selectedRow);
            this.isInEditMode = false;

            this.sendSearchRequest();
            this.toastService.success(this.translateService.instant("AirportMaintenance.Toast.SaveSuccess"));
          },
          error: response => {
            this.isLoading = false;
            this.newDatas = [];
            this.updatedDatas = [];
            this.toastService.error(this.translateService.instant('AirportMaintenance.Toast.SaveFail') + response.status);
          }
        });
      }
    })
  }

  restoreTableDate(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('AirportMaintenance.Confirm.Header'),
      message: this.translateService.instant('AirportMaintenance.Confirm.Restore'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.restoreData();

        // Update table to last saved data
        this.tableData = deepCopy(this.backupTableData)!;
        this.clearTableEdit();
        this.table.initRowEdit(this.selectedRow);

        this.toastService.success(this.translateService.instant('AirportMaintenance.Toast.RestoreSuccess'))
      }
    })
  }

  switchEditMode(): void {
    if (this.isInEditMode) {
      this.confirmationService.confirm({
        header: this.translateService.instant('AirportMaintenance.Confirm.Header'),
        message: this.translateService.instant('AirportMaintenance.Confirm.Exit'),
        icon: 'pi pi-info-circle',
        accept: () => {
          this.isInEditMode = false;
          this.restoreData();

          // Update table to last saved data
          this.tableData = deepCopy(this.backupTableData)!;
          this.clearTableEdit();

          this.toastService.success(this.translateService.instant('AirportMaintenance.Toast.ExitSuccess'))
        }
      })
    }
    else {
      this.isInEditMode = true;
      if (!isNullOrEmpty(this.selectedRow)) {
        this.table.initRowEdit(this.selectedRow);
      }
    }
  }

  onRowSelect(rowData: AirportMaintenanceTableData) {
    if (this.isInEditMode) {
      // Keep the selectedRow variable is pointing at the user's selected row
      // Whether is select or deselect
      this.selectedRow = rowData;

      // Reselect after user click on the same row
      if (this.previousSelectedRow != null && this.selectedRow.dataKey == this.previousSelectedRow.dataKey) {
        this.updateTableHighlight(this.previousSelectedRow);
        return;
      }

      // Change the current selected row to the user's selected row
      this.table.cancelRowEdit(this.previousSelectedRow)
      this.table.initRowEdit(this.selectedRow);
      this.previousSelectedRow = this.selectedRow;

    }
    else {
      this.previousSelectedRow = this.selectedRow;
    }
  }

  onTablePageChanging(event: { first: number, rows: number }) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / event.rows;
    this.isLoading = true;
    this.sendSearchRequest();
  }

  onTableSorting(event: { field: string, order: number }) {
    if (this.tableData.length != 0) {
      if (event.field == 'locatedCityName') {
        this.sortOrder.property = 'commonAttr01'
      }
      else {
        this.sortOrder.property = event.field;
      }
      this.sortOrder.direction = event.order;
      this.isLoading = true;
      this.sendSearchRequest();
    }
  }

  getCityNameBySeqNo(seqNo: number | null): { name: string | null, enName: string | null } {
    if (seqNo == null) {
      return { name: null, enName: null };
    }

    let city = this.cityOptions.find(option => option.seqNo == seqNo) ?? null;

    return { name: city?.name ?? null, enName: city?.enName ?? null };
  }

  private setDataByServiceResponse(response: PageResp<AirportMaintenanceTableData>) {
    this.tableData = deepCopy(response.content) ?? [];
    this.backupTableData = deepCopy(this.tableData) ?? [];
    this.selectedRow = this.tableData[0];
    this.previousSelectedRow = this.selectedRow;
    this.updateTableHighlight(this.selectedRow)

    if (this.isInEditMode) {
      this.table.initRowEdit(this.selectedRow);
    }

    // Change page Info
    this.pageInfo.totalRecords = response.totalElements;
    this.pageInfo.totalPages = this.pageInfo.totalRecords / this.pageInfo.pageSize;
  }

  private clearTable() {
    this.tableData = [];
    this.backupTableData = [];
    this.selectedRow = null;
    this.previousSelectedRow = this.selectedRow;
    this.pageInfo.totalRecords = 0;
    this.pageInfo.totalPages = 0;
  }

  // Cancel current table's row editing
  private clearTableEdit() {
    this.table.cancelRowEdit(this.selectedRow);
    this.selectedRow = this.tableData[0];
    this.previousSelectedRow = this.selectedRow;

    //Avoid the highlighted part leave on the previous row
    this.updateTableHighlight(this.selectedRow);
  }

  private restoreData(): void {
    // Clear user changed action
    this.newDataKeyId = 0;
    this.newDatas = [];
    this.updatedDatas = [];
    this.deletedDataSeqNos = [];
    this.table.resetScrollTop();
  }

  private createGetRequestBean(): AirportMaintenanceGetRequest {
    let request: AirportMaintenanceGetRequest = new AirportMaintenanceGetRequest();
    request.airportCode = this.searchFormGroup.value.airportCode;
    request.airportName = this.searchFormGroup.value.airportName;
    request.locatedCitySeqNo = this.convertEmptyNumberArrayToNull(this.searchFormGroup.value.locatedCitySeqNo);
    request.pageIndex = this.pageInfo.pageIndex;
    request.pageSize = this.pageInfo.pageSize;
    request.sortOrder = this.sortOrder;
    return request;
  }

  private checkNewAndUpdatedRow(): void {
    let saveDataIndex = 0;

    if (saveDataIndex < this.tableData.length) {
      // Check if any added row by dataKey property
      // Added row's dataKey format : --id
      for (const tableData of this.tableData) {
        if (tableData.dataKey[0] != '-') {
          break;
        }
        this.addToNewDataList(tableData);
        saveDataIndex++;
      }

      // Reverse for maintain order of user adding new row
      this.newDatas.reverse();

      // Compare backup table data to current table data
      for (const backupData of this.backupTableData) {
        if (this.deletedDataSeqNos.indexOf(backupData.seqNo) == -1) {
          if (JSON.stringify(this.tableData[saveDataIndex]) != JSON.stringify(backupData)) {
            this.addToUpdateList(this.tableData[saveDataIndex]);
          }
          saveDataIndex++;
        }
      }
    }
  }

  // Control the selection highlighting back to the row
  private updateTableHighlight(currentRow: AirportMaintenanceTableData | null): void {
    if (!currentRow) {
      return;
    }
    this.table.selection = currentRow;
    this.table.selectionKeys = {};
    this.table.selectionKeys[currentRow.dataKey] = 1;
    this.table.tableService.onSelectionChange();
  }

  private addToNewDataList(rowData: AirportMaintenanceTableData): void {
    let newData = new AirportMaintenanceSaveBean();
    newData.seqNo = rowData.seqNo;
    newData.airportCode = rowData.airportCode;
    newData.airportName = rowData.airportName;
    newData.locatedCityName = this.getCityNameBySeqNo(rowData.locatedCitySeqNo).enName;
    newData.locatedCitySeqNo = rowData.locatedCitySeqNo;
    newData.isActivated = rowData.isActivated;
    newData.createUser = rowData.createUser;
    newData.createDt = rowData.createDt;
    newData.updateUser = rowData.updateUser;
    newData.updateDt = rowData.updateDt;
    this.newDatas.push(newData);
  }

  private addToUpdateList(rowData: AirportMaintenanceTableData): void {
    let updatedData = new AirportMaintenanceSaveBean();
    updatedData.seqNo = rowData.seqNo;
    updatedData.airportCode = rowData.airportCode;
    updatedData.airportName = rowData.airportName;
    updatedData.locatedCityName = this.getCityNameBySeqNo(rowData.locatedCitySeqNo).enName;
    updatedData.locatedCitySeqNo = rowData.locatedCitySeqNo;
    updatedData.isActivated = rowData.isActivated;
    updatedData.createUser = rowData.createUser;
    updatedData.createDt = rowData.createDt;
    updatedData.updateUser = this.userService.userInfo.userName;
    updatedData.updateDt = new Date();
    this.updatedDatas.push(updatedData);
  }

  private addToDeletedList(seqNo: number): void {
    this.deletedDataSeqNos.push(seqNo);
  }

  private convertEmptyNumberArrayToNull(value: number[]): number[] | null {
    if (isNullOrEmpty(value) || value.length <= 0) {
      return null;
    }
    return value;
  }
}
